import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type { LoginRequest, AuthResponse, User, Deceased, UserDto, CreateUserDto, UpdateUserDto, SetInitialPasswordRequest } from '../types';
import { createStore } from 'devextreme-aspnet-data-nojquery';

// Configure axios defaults
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';

console.log('API Base URL configured as:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for cookie-based authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    // If sending FormData, delete Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    console.log(`Making ${config.method?.toUpperCase()} request to: ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Redirect to login or handle unauthorized access
      window.location.href = '/admin/login';
    }
    
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', credentials);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  checkAuth: async (): Promise<{ isAuthenticated: boolean; user?: User }> => {
    const response = await api.get('/auth/check');
    return response.data;
  },

  checkPasswordStatus: async (email: string): Promise<{ requiresPasswordSetup: boolean; user?: User }> => {
    const response = await api.post('/auth/check-password-status', { email });
    return response.data;
  },

  setInitialPassword: async (request: SetInitialPasswordRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/set-initial-password', request);
    return response.data;
  },
};

export const deceasedApi = {
  // DevExtreme Data Store for server-side processing
  createStore: () => createStore({
    key: 'Id',
    loadUrl: `${API_BASE_URL}/deceased`,
    insertUrl: `${API_BASE_URL}/deceased`,
    updateUrl: `${API_BASE_URL}/deceased`,
    deleteUrl: `${API_BASE_URL}/deceased`,
    onBeforeSend: (_method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
    },
  }),

  getAll: async (): Promise<Deceased[]> => {
    const response: AxiosResponse<Deceased[]> = await api.get('/deceased');
    return response.data;
  },

  getById: async (id: number): Promise<Deceased> => {
    const response: AxiosResponse<Deceased> = await api.get(`/deceased/${id}`);
    return response.data;
  },

  create: async (deceased: Partial<Deceased>): Promise<Deceased> => {
    const response: AxiosResponse<Deceased> = await api.post('/deceased', deceased);
    return response.data;
  },

  update: async (id: number, deceased: Partial<Deceased>): Promise<void> => {
    await api.put(`/deceased/${id}`, deceased);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/deceased/${id}`);
  },

  // Autocomplete data sources
  getFirstNames: async (): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/firstnames');
    return response.data;
  },

  getLastNames: async (): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/lastnames');
    return response.data;
  },

  getMiddleNames: async (): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/middlenames');
    return response.data;
  },

  getMaidenNames: async (): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/maidennames');
    return response.data;
  },

  getGraveTypes: async (): Promise<string[]> => {
    const response = await api.get('/deceased/autocomplete/gravetypes');
    return response.data;
  },
};

export const usersApi = {
  createStore: () => createStore({
    key: 'id',
    loadUrl: `${API_BASE_URL}/users`,
    insertUrl: `${API_BASE_URL}/users`,
    updateUrl: `${API_BASE_URL}/users`,
    deleteUrl: `${API_BASE_URL}/users`,
    onBeforeSend: (_method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
    },
  }),

  getById: async (id: string): Promise<UserDto> => {
    const response: AxiosResponse<UserDto> = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (user: CreateUserDto): Promise<UserDto> => {
    const response: AxiosResponse<UserDto> = await api.post('/users', user);
    return response.data;
  },

  update: async (id: string, user: UpdateUserDto): Promise<void> => {
    await api.put(`/users/${id}`, user);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  toggleStatus: async (id: string): Promise<void> => {
    await api.post(`/users/${id}/toggle-status`);
  },

  resetPassword: async (id: string): Promise<void> => {
    await api.post(`/users/${id}/reset-password`);
  },
};

export const searchApi = {
  search: async (params: {
    name?: string;
    birthYear?: number;
    deathYear?: number;
    page?: number;
    pageSize?: number;
  }) => {
    const response = await api.get('/search', { params });
    return response.data;
  },

  getDetails: async (id: number) => {
    const response = await api.get(`/search/${id}/details`);
    return response.data;
  },

  getSuggestions: async (term: string) => {
    const response = await api.get('/search/suggestions', { params: { term } });
    return response.data;
  },
};

export const cemeteryApi = {
  getCemeteryData: async () => {
    const response = await api.get('/cemetery');
    return response.data;
  },

  getPublicCemeteryData: async () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';
    const response = await fetch(`${baseUrl}/cemetery/public`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to load cemetery data');
    }
    return response.json();
  },

  uploadPhoto: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('cemetery/upload-photo', formData);
    return response.data;
  },

  saveCemeteryData: async (data: {
    photoFileName: string;
    photoWidth: number;
    photoHeight: number;
    northDirectionX?: number;
    northDirectionY?: number;
    referencePoints: Array<{
      name: string;
      x: number;
      y: number;
    }>;
  }) => {
    const response = await api.post('/cemetery', data);
    return response.data;
  },

  updateCemeteryData: async (id: number, data: {
    northDirectionX?: number;
    northDirectionY?: number;
    referencePoints: Array<{
      name: string;
      x: number;
      y: number;
    }>;
  }) => {
    const response = await api.put(`/cemetery/${id}`, data);
    return response.data;
  },

  deleteCemeteryData: async (id: number) => {
    await api.delete(`/cemetery/${id}`);
  },
};

export const graveSitesApi = {
  // DevExtreme Data Store for server-side processing
  createStore: () => createStore({
    key: 'graveCode',
    loadUrl: `${API_BASE_URL}/gravesites`,
    insertUrl: `${API_BASE_URL}/gravesites`,
    updateUrl: `${API_BASE_URL}/gravesites`,
    deleteUrl: `${API_BASE_URL}/gravesites`,
    onBeforeSend: (_method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
    },
  }),

  getAll: async () => {
    const response = await api.get('/gravesites');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/gravesites/${id}`);
    return response.data;
  },

  create: async (graveSite: any) => {
    const response = await api.post('/gravesites', graveSite);
    return response.data;
  },

  update: async (id: number, graveSite: any) => {
    const response = await api.put(`/gravesites/${id}`, graveSite);
    return response.data;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/gravesites/categories');
    return response.data;
  },

  updateLocation: async (id: number, locationX: number | null, locationY: number | null) => {
    const response = await api.put(`/gravesites/${id}/location`, { locationX, locationY });
    return response.data;
  },

  updateGeoLocation: async (id: number, latitude: number, longitude: number) => {
    const response = await api.put(`/gravesites/${id}/geolocation`, { latitude, longitude });
    return response.data;
  },

  uploadPhoto: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response = await api.post(`/gravesites/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deletePhoto: async (id: number) => {
    const response = await api.delete(`/gravesites/${id}/photo`);
    return response.data;
  },

  getCemeteryPhoto: async () => {
    const response = await api.get('/gravesites/cemetery-photo');
    return response.data;
  },

  exportAll: async () => {
    const response = await api.get('/gravesites/export', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    link.setAttribute('download', `Pokopani_${date}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportSelected: async (ids: number[]) => {
    const response = await api.post('/gravesites/export', { ids }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    link.setAttribute('download', `Pokopani_${date}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getDeceasedByGrave: (graveSiteId: number) => createStore({
    key: 'id',
    loadUrl: `${API_BASE_URL}/deceased/by-gravesite/${graveSiteId}`,
    insertUrl: `${API_BASE_URL}/deceased`,
    updateUrl: `${API_BASE_URL}/deceased`,
    deleteUrl: `${API_BASE_URL}/deceased`,
    onBeforeSend: (method, ajaxOptions) => {
      ajaxOptions.xhrFields = { withCredentials: true };
      
      // For INSERT operations, ensure graveSiteId is included
      if (method === 'POST' && ajaxOptions.data) {
        const formData = new FormData();
        
        // Parse existing values if they exist
        let values = {};
        if (typeof ajaxOptions.data === 'string') {
          const params = new URLSearchParams(ajaxOptions.data);
          const valuesParam = params.get('values');
          if (valuesParam) {
            values = JSON.parse(valuesParam);
          }
        }
        
        // Add graveSiteId to the values
        values = { ...values, graveSiteId };
        
        // Rebuild form data
        formData.append('values', JSON.stringify(values));
        ajaxOptions.data = formData;
      }
    },
  }),
};

export default api;